import React from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { stateIsLoading, useUserVideoCourse, useVideoCourse } from '@guryou/redux';
import { Icon } from '../../components/shared';
import VideoCourseFiles from './VideoCourseFiles';
import AnimatedBackground from 'components/shared/AnimatedBackground';

const PublicVideoCourseFiles = () => {
  const { id } = useParams();
  const { t } = useTranslation();
  const videoCourse = useVideoCourse(id);
  const userVideoCourse = useUserVideoCourse(id);

  if (userVideoCourse?.data) {
    return <VideoCourseFiles id={id} videoCourse={userVideoCourse.data} isEnrolled />;
  }

  if (stateIsLoading(videoCourse)) {
    return (
      <div className="min-h-screen bg-white">
        {/* Hero Skeleton */}
        <AnimatedBackground className="mt-32">
          <div className="relative overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-r from-black/50 to-transparent"></div>
            <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
                {/* Content Skeleton */}
                <div className="text-white">
                  <div className="h-12 bg-gray-600 rounded-lg mb-6 animate-pulse"></div>
                  <div className="space-y-3 mb-6">
                    <div className="h-4 bg-gray-600 rounded animate-pulse"></div>
                    <div className="h-4 bg-gray-600 rounded animate-pulse"></div>
                    <div className="h-4 bg-gray-600 rounded w-3/4 animate-pulse"></div>
                  </div>
                  <div className="flex items-center mb-6">
                    <div className="h-4 w-4 bg-gray-600 rounded mr-3 animate-pulse"></div>
                    <div className="h-4 w-32 bg-gray-600 rounded animate-pulse"></div>
                  </div>
                  <div className="h-8 w-24 bg-gray-600 rounded animate-pulse"></div>
                </div>

                {/* Image Skeleton */}
                <div className="flex justify-center lg:justify-end">
                  <div className="w-full max-w-md h-64 bg-gray-600 rounded-2xl animate-pulse"></div>
                </div>
              </div>
            </div>
          </div>
        </AnimatedBackground>
        {/* Content Section Skeleton */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="mb-8">
            <div className="h-8 w-48 bg-gray-300 rounded mb-4 animate-pulse"></div>
            <div className="w-20 h-1 bg-gray-300 rounded-full animate-pulse"></div>
          </div>
          <div className="max-w-4xl">
            <div className="space-y-6">
              {[1, 2, 3].map(i => (
                <div key={i} className="bg-white rounded-2xl shadow-lg border border-gray-200 overflow-hidden">
                  <div className="p-6">
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center space-x-3">
                        <div className="w-8 h-8 bg-gray-200 rounded-full animate-pulse"></div>
                        <div className="h-6 bg-gray-200 rounded w-48 animate-pulse"></div>
                      </div>
                      <div className="h-6 bg-gray-200 rounded w-16 animate-pulse"></div>
                    </div>
                    <div className="space-y-3">
                      {[1, 2, 3].map(j => (
                        <div key={j} className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg">
                          <div className="w-6 h-6 bg-gray-200 rounded animate-pulse"></div>
                          <div className="h-4 bg-gray-200 rounded flex-1 animate-pulse"></div>
                          <div className="w-12 h-4 bg-gray-200 rounded animate-pulse"></div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!videoCourse?.data) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Icon name="exclamation-triangle" size="xl" className="text-gray-400 mb-4" />
          <h2 className="text-2xl font-bold text-gray-900 mb-2">{t('VIDEO_COURSE_PAGE.somethingWentWrong')}</h2>
          <p className="text-gray-600">{t('VIDEO_COURSE_PAGE.courseNotFound')}</p>
        </div>
      </div>
    );
  }

  return <VideoCourseFiles id={id} videoCourse={videoCourse.data} />;
};

export default PublicVideoCourseFiles;
